import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Form, Input, Select, Button, message, Typography, Space, Upload, Table, Popconfirm, Modal, Card } from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SyncOutlined
} from '@ant-design/icons';
import api from '../../utils/api';
import { usePermissions } from '../../hooks/usePermissions';
import { PERMISSION_LEVELS } from '../../const/roles';
import { useAuth } from '../../context/AuthContext';

const { Option } = Select;
const { Text } = Typography;

const mapInvolvedAssetToCamelCase = (asset) => ({
  id: asset.id,
  ticketId: asset.ticket_id,
  assetType: asset.asset_type,
  model: asset.model,
  serialNumber: asset.serial_number,
  ipAddress: asset.ip_address,
  hostName: asset.host_name,
  addedByUserId: asset.added_by_user_id,
  createdAt: asset.created_at,
  updatedAt: asset.updated_at,
  addedByFirstName: asset.added_by_first_name,
  addedByLastName: asset.added_by_last_name
});

const mapInvolvedAssetToSnakeCase = (asset) => ({
  // Fixed: Removed redundant camelCase keys.
  id: asset.id,
  ticket_id: asset.ticketId,
  asset_id: asset.assetId,
  user_id: asset.userId,
  added_by_user_id: asset.userId,
  asset_type: asset.assetType,
  model: asset.model,
  serial_number: asset.serialNumber,
  ip_address: asset.ipAddress,
  host_name: asset.hostName
});

const validateIpAddress = (rule, value) => {
  if (!value || value === 'N/A') {
    return Promise.resolve();
  }
  const ipv4Regex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|x)$/;
  if (ipv4Regex.test(value)) {
    return Promise.resolve();
  }
  console.warn('Non-standard IP address entered:', value);
  return Promise.resolve();
};

const TicketInvolvedAssetsManager = ({
  ticketId,
  user,
  hasRole,
  ROLE_DEFINITIONS,
  ROLES,
  notificationApi,
  assignees,
  ticketData
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [assets, setAssets] = useState([]);
  const [availableAssets, setAvailableAssets] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const [assetTypes, setAssetTypes] = useState([]);
  const [filteredModels, setFilteredModels] = useState([]);
  const [filteredSerials, setFilteredSerials] = useState([]);
  const [filteredHosts, setFilteredHosts] = useState([]);
  const [filteredIPs, setFilteredIPs] = useState([]);

  const { canView, canEdit, isGodMode, canEditTicket } = usePermissions(user);

  const showError = useCallback((title, content) => {
    notificationApi.error({ message: title, description: content });
  }, [notificationApi]);

  const showSuccess = useCallback((title) => {
    notificationApi.success({ message: title });
  }, [notificationApi]);

// Update the canManage computation in TicketInvolvedAssetsManager.js
const canManage = useMemo(() => {
  if (!user || !ticketData) return false;
  
  console.log('User Organization Check:', {
    userOrgId: user.organizationId,
    userRole: user.roleName,
    userId: user.id,
    ticketCreatorId: ticketData.createdByUserId,
    ticketAssigneeId: ticketData.assignedToUserId,
    ticketOrgId: ticketData.organizationId,
    isSameOrg: user.organizationId === ticketData.organizationId,
    ticketStatus: ticketData.currentStatusId // Add ticket status for debugging
  });
  
  // NEW: If ticket is closed (status ID: 6), only allow System Admin and Internal Supervisor
  if (ticketData.currentStatusId === 6) { // Closed status
    const isSystemAdmin = user.roleName === 'System Admin';
    const isInternalSupervisor = user.roleName === 'Internal Staff Supervisor';
    
    // Only System Admin and Internal Supervisor can manage assets for closed tickets
    console.log('Asset Manager: Closed ticket - only System Admin/Internal Supervisor can manage');
    return isSystemAdmin || isInternalSupervisor;
  }
  
  // System Admin and Internal Supervisor always have full access for non-closed tickets
  if (user.roleName === 'System Admin' || user.roleName === 'Internal Staff Supervisor') {
    return true;
  }
  
  // Creator or assignee can always manage assets for non-closed tickets
  const isCreator = user.id === ticketData.createdByUserId;
  const isAssignee = user.id === ticketData.assignedToUserId;
  if (isCreator || isAssignee) {
    console.log('Asset Manager: Creator/Assignee - ALLOW');
    return true;
  }
  
  const isSameOrganization = user.organizationId && 
                           ticketData.organizationId && 
                           user.organizationId === ticketData.organizationId;  

  // For same organization users with appropriate roles
  const isPartnerSupervisor = user.roleName === 'Partner Supervisor';
  const isCustomerSupervisor = user.roleName === 'Customer Supervisor';
  
  // Additional check: make sure the user actually has an organization
  const result = isSameOrganization && (isPartnerSupervisor || isCustomerSupervisor);
  console.log('Asset Manager: Supervisor Check -', result ? 'ALLOW' : 'DENY');
  
  return result;
}, [user, ticketData]);

  const fetchData = useCallback(async () => {
    if (!canView('tickets')) return;
    
    try {
      setLoading(true);
      const [assetsRes, involvedRes] = await Promise.all([
        api.get('/api/assets'),
        api.get(`/api/tickets/${ticketId}/involved-assets`)
      ]);
      
      setAvailableAssets(assetsRes.data.data || []);
      // FIX: Access the 'data' property of the involvedRes response.
      // The API returns an object { success: true, data: [...] }.
      // We need to map over the 'data' property, which contains the array of involved assets.
      setAssets((involvedRes.data.data || []).map(mapInvolvedAssetToCamelCase));
      setAssetTypes([...new Set((assetsRes.data.data || []).map(a => a.asset_type))]);
    } catch (error) {
      console.error('Failed to load assets:', error);
      message.error(error.response?.data?.message || 'Failed to load assets');
    } finally {
      setLoading(false);
    }
  }, [ticketId, canView]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateFilters = useCallback((type, model, serial) => {
    let filtered = availableAssets;
    
    if (type) {
      filtered = filtered.filter(a => a.asset_type === type);
      const models = [...new Set(filtered.map(a => a.model))];
      setFilteredModels(models);
    }
    
    if (model) {
      filtered = filtered.filter(a => a.model === model);
      const serials = filtered.map(a => a.serial_number);
      
      const uniqueSerials = [...new Set(serials)];
      setFilteredSerials(uniqueSerials);

      const hosts = [...new Set(filtered.map(a => a.host_name))].filter(Boolean);
      const ips = [...new Set(filtered.map(a => a.ip_address))].filter(Boolean);
      setFilteredHosts(hosts);
      setFilteredIPs(ips);
    }
    
    if (serial) {
      const filteredBySerial = availableAssets.filter(a => 
        a.asset_type === type && a.model === model && a.serial_number === serial
      );
      const hosts = [...new Set(filteredBySerial.map(a => a.host_name))].filter(Boolean);
      const ips = [...new Set(filteredBySerial.map(a => a.ip_address))].filter(Boolean);
      setFilteredHosts(hosts);
      setFilteredIPs(ips);
    }
  }, [availableAssets]);

  const handleTypeChange = (type) => {
    form.setFieldsValue({ model: null, serialNumber: null, hostName: null, ipAddress: null });
    updateFilters(type);
  };

  const handleModelChange = (model) => {
    form.setFieldsValue({ serialNumber: null, hostName: null, ipAddress: null });
    updateFilters(form.getFieldValue('assetType'), model);
    const assetType = form.getFieldValue('assetType');
    const isHostnameBased = assetType === 'Virtual Machine' || assetType === 'Applications';
    if (isHostnameBased) {
        form.setFieldsValue({ serialNumber: 'N/A' });
    }
  };
  
  const handleSerialChange = (serial) => {
    form.setFieldsValue({ hostName: null, ipAddress: null });
    updateFilters(
      form.getFieldValue('assetType'),
      form.getFieldValue('model'),
      serial
    );
    
    const matches = availableAssets.filter(a => 
      a.serial_number === serial &&
      a.asset_type === form.getFieldValue('assetType') &&
      a.model === form.getFieldValue('model')
    );
    
    if (matches.length === 1) {
      form.setFieldsValue({
        hostName: matches[0].host_name,
        ipAddress: matches[0].ip_address
      });
    }
  };

  const handleHostChange = (host) => {
    const assetType = form.getFieldValue('assetType');
    const model = form.getFieldValue('model');
    const match = availableAssets.find(a => 
      a.asset_type === assetType && 
      a.model === model &&
      a.host_name === host
    );
    if (match) {
      form.setFieldsValue({ ipAddress: match.ip_address });
    }
  };

  const handleIpChange = (ip) => {
    const assetType = form.getFieldValue('assetType');
    const model = form.getFieldValue('model');
    const match = availableAssets.find(a => 
      a.asset_type === assetType && 
      a.model === model &&
      a.ip_address === ip
    );
    if (match) {
      form.setFieldsValue({ hostName: match.host_name });
    }
  };

  const handleAddAsset = () => {
    setIsAdding(true);
    setEditingId('new');
    form.resetFields();
    setFilteredModels([]);
    setFilteredSerials([]);
    setFilteredHosts([]);
    setFilteredIPs([]);
    form.setFieldsValue({
      assetType: null,
      model: null,
      serialNumber: null,
      ipAddress: null,
      hostName: null
    });
  };

const handleSave = async () => {
  try {
    const values = await form.validateFields();
    setLoading(true);

    const token = localStorage.getItem('authToken');

    const isNewAsset = !editingId || editingId === 'new';
    
    // Find the matching asset
    const matchingAsset = availableAssets.find(asset => 
      asset.asset_type === values.assetType &&
      asset.model === values.model &&
      (asset.serial_number === values.serialNumber || 
       asset.host_name === values.hostName)
    );

    if (isNewAsset && !matchingAsset) {
      throw new Error('Could not find matching asset in system');
    }

    // Check asset type
    const isHostnameBased = values.assetType === 'Virtual Machine' || 
                           values.assetType === 'Applications';

    const payload = {
      ticket_id: Number(ticketId),
      asset_id: isNewAsset ? Number(matchingAsset.id) : Number(editingId),
      user_id: Number(user.id),
      asset_type: values.assetType,
      model: values.model,
      serial_number: isHostnameBased ? 'N/A' : values.serialNumber,
      ip_address: values.ipAddress,
      host_name: values.hostName
    };

    console.log('Final payload:', payload);

    let response;
    if (isNewAsset) {
      response = await api.post(
        `/api/tickets/${ticketId}/involved-assets`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );
    } else {
      response = await api.put(
        `/api/tickets/${ticketId}/involved-assets/${editingId}`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );
    }

    // FIXED: The API response likely contains the new asset in a `data` property.
    // We need to access that property before mapping to avoid creating an invalid record.
    const newAssetRecord = mapInvolvedAssetToCamelCase(response.data.data);
    
    setAssets(prevAssets => isNewAsset 
      ? [newAssetRecord, ...prevAssets] 
      : prevAssets.map(a => a.id === editingId ? newAssetRecord : a)
    );

    showSuccess(isNewAsset ? 'Asset added successfully' : 'Asset updated successfully');
    resetForm();
  } catch (error) {
    console.error('Detailed error:', {
      message: error.message,
      response: error.response?.data,
      request: error.config?.data,
      stack: error.stack
    });
    
    const errorMessage = error.response?.data?.message || 
      'Failed to save asset. Please check your data and try again.';
    showError('Operation Failed', errorMessage);
  } finally {
    setLoading(false);
  }
};

const resetForm = () => {
  form.resetFields();
  setEditingId(null);
  setIsAdding(false);
  setFilteredModels([]);
  setFilteredSerials([]);
  setFilteredHosts([]);
  setFilteredIPs([]);
};

const handleDelete = async (assetId) => {
  try {
    setIsDeleting(true);
    const token = localStorage.getItem('authToken');
    
    const response = await api.delete(
      `/api/tickets/${ticketId}/involved-assets/${assetId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );
    
    if (response.status === 200 || response.status === 204) {
      showSuccess('Asset deleted successfully!');
      setAssets(prev => prev.filter(asset => asset.id !== assetId));
    } else {
      throw new Error('Failed to delete asset');
    }
  } catch (error) {
    if (error.response?.status === 401) {
      showError('Session Expired', 'Your session has expired. Please log in again.');
      return;
    }
    console.error('Delete error:', error);
    showError('Error', error.response?.data?.message || 'Failed to delete asset');
  } finally {
    setIsDeleting(false);
  }
};

  const handleEdit = (asset) => {
    setEditingId(asset.id);
    form.setFieldsValue(asset);
    updateFilters(
      asset.assetType,
      asset.model,
      asset.serialNumber
    );
  };

  const columns = [
    {
      title: 'Asset Type',
      dataIndex: 'assetType',
      key: 'assetType',
      width: '15%',
      render: (text, record) => {
        const isEditing = record.id === editingId || record.isNew;
        return isEditing ? (
          <Form.Item
            name="assetType"
            rules={[{ required: true, message: 'Asset type is required!' }]}
            initialValue={text}
            style={{ margin: 0 }}
          >
            <Select
              placeholder="Select Asset Type"
              onChange={handleTypeChange}
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {assetTypes.map(type => (
                <Option key={type} value={type}>{type}</Option>
              ))}
            </Select>
          </Form.Item>
        ) : text;
      },
    },
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model',
      width: '15%',
      render: (text, record) => {
        const isEditing = record.id === editingId || record.isNew;
        return isEditing ? (
          <Form.Item
            name="model"
            rules={[{ required: true, message: 'Model is required!' }]}
            initialValue={text}
            style={{ margin: 0 }}
          >
            <Select
              placeholder="Select Model"
              onChange={handleModelChange}
              disabled={!form.getFieldValue('assetType')}
              showSearch
            >
              {filteredModels.map(model => (
                <Option key={model} value={model}>{model}</Option>
              ))}
            </Select>
          </Form.Item>
        ) : text;
      },
    },
    {
      title: 'Serial No.',
      dataIndex: 'serialNumber',
      key: 'serialNumber',
      width: '15%',
      render: (text, record) => {
        const isEditing = record.id === editingId || record.isNew;
        const assetType = form.getFieldValue('assetType');
        const isHostnameBased = assetType === 'Virtual Machine' || assetType === 'Applications';
        
        return isEditing ? (
          <Form.Item
            name="serialNumber"
            initialValue={text}
            style={{ margin: 0 }}
            rules={isHostnameBased ? [] : [{ required: true, message: 'Serial number is required!' }]}
          >
            {isHostnameBased ? (
              <Input disabled value="N/A" />
            ) : (
              <Select
                placeholder={text === 'N/A' ? 'N/A' : 'Select Serial'}
                onChange={handleSerialChange}
                disabled={!form.getFieldValue('model')}
                showSearch
              >
                {filteredSerials.map(serial => (
                  <Option key={serial} value={serial}>
                    {serial === 'N/A' ? 'N/A' : serial}
                  </Option>
                ))}
              </Select>
            )}
          </Form.Item>
        ) : text;
      },
    },
    {
      title: 'IP Address',
      dataIndex: 'ipAddress',
      key: 'ipAddress',
      width: '15%',
      render: (text, record) => {
        const isEditing = record.id === editingId || record.isNew;
        const assetType = form.getFieldValue('assetType');
        const isHostnameBased = assetType === 'Virtual Machine' || assetType === 'Applications';
        
        return isEditing ? (
          <Form.Item
            name="ipAddress"
            rules={[{ validator: validateIpAddress }]}
            initialValue={text}
            style={{ margin: 0 }}
          >
            {isHostnameBased ? (
              <Select
                placeholder="Select IP Address"
                onChange={handleIpChange}
                disabled={!form.getFieldValue('model')}
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
              >
                {filteredIPs.map(ip => (
                  <Option key={ip} value={ip}>{ip}</Option>
                ))}
              </Select>
            ) : (
              <Input 
                placeholder="Enter IP Address" 
                disabled={!form.getFieldValue('serialNumber')}
              />
            )}
          </Form.Item>
        ) : text;
      },
    },
    {
      title: 'Host Name',
      dataIndex: 'hostName',
      key: 'hostName',
      width: '15%',
      render: (text, record) => {
        const isEditing = record.id === editingId || record.isNew;
        const assetType = form.getFieldValue('assetType');
        const isHostnameBased = assetType === 'Virtual Machine' || assetType === 'Applications';
        
        return isEditing ? (
          <Form.Item
            name="hostName"
            rules={[{ required: true, message: 'Host name is required!' }]}
            initialValue={text}
            style={{ margin: 0 }}
          >
            {isHostnameBased ? (
              <Select
                placeholder="Select Host Name"
                onChange={handleHostChange}
                disabled={!form.getFieldValue('model')}
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
              >
                {filteredHosts.map(host => (
                  <Option key={host} value={host}>{host}</Option>
                ))}
              </Select>
            ) : (
              <Input 
                placeholder="Enter Host Name" 
                disabled={!form.getFieldValue('serialNumber')}
              />
            )}
          </Form.Item>
        ) : text;
      },
    },
    {
      title: 'Added By',
      dataIndex: 'addedByUserId',
      key: 'addedByUserId',
      width: '10%',
      render: (text, record) => {
        if (record.id === editingId || record.isNew) {
          return `${user.firstName} ${user.lastName}`;
        }
        
        const addedByUser = assignees?.find(a => a.id === text) || 
                            (text === user.id ? user : null);
        
        return addedByUser ? 
          `${addedByUser.firstName} ${addedByUser.lastName}` : 
          'N/A';
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '15%',
      render: (_, record) => {
        if (record.id === editingId || record.isNew) {
          return (
            <Space size="middle">
              <Button type="link" onClick={handleSave} loading={loading}>
                Save
              </Button>
              <Button type="link" onClick={resetForm}>
                Cancel
              </Button>
            </Space>
          );
        }

        return (
          <Space size="small">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              disabled={!canManage}
            />
          <Popconfirm
            title="Delete this asset?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
            disabled={!canManage}
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              disabled={!canManage || isDeleting}
              loading={isDeleting}
            />
          </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <Card
      title="Involved Assets"
      extra={
        <Space>
          <Button icon={<SyncOutlined />} onClick={fetchData} loading={loading}>
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddAsset}
            disabled={!canManage}
          >
            Add Asset
          </Button>
        </Space>
      }
    >
      <Form form={form} component={false}>
        <Table
          columns={columns}
          dataSource={isAdding ? [{ id: 'new', isNew: true }, ...assets] : assets}
          rowKey="id"
          loading={loading}
          pagination={false}
          bordered
        />
      </Form>
    </Card>
  );
};

export default TicketInvolvedAssetsManager;